import { buildImgixUrl } from 'integrations/imgix';
import FileUpload from 'lib/file-upload';
import { createApi } from 'unsplash-js';

const unsplash = createApi({
  accessKey: process.env.NEXT_PUBLIC_UNSPLASH_ACCESS_KEY,
});

type Options = {
  pageNumber: number;
  numberOfImages: number;
  orientation: 'landscape' | 'portrait';
};

/**
 *
 * @param {string} value - The search text
 * @param {Object} option - ImageList parameters
 * @param {number} option.pageNumber - The page number
 * @param {number} option.numberOfImages - Number of images to fetch in one call
 * @param {number} option.orientation - Orientation of the image portrait | landscape
 */
export const loadImages = async (
  value = 'community',
  options: Options = {
    pageNumber: 1,
    numberOfImages: 25,
    orientation: 'landscape',
  },
) => {
  const { pageNumber, numberOfImages, orientation } = options;

  const result = await unsplash.search.getPhotos({
    query: value,
    page: pageNumber,
    perPage: numberOfImages,
    orientation,
  });

  if (result.type === 'success') {
    const { response } = result;
    return response.results;
  }

  return [];
};

/**
 *
 * @param {string} imageUrl - Download link of the image { links: {download: 'download url' }}
 * @param {string} id -  Image id
 */
export const uploadImage = async (imageUrl, id) => {
  const defaultImageSize = 2000;
  const updatedImageSizeUrl = `${imageUrl}?w=${defaultImageSize}`;

  const awsImageUrl = await FileUpload.perform(updatedImageSizeUrl);

  await triggerDownloadTracking(id);

  return awsImageUrl;
};

/**
 *
 * @param {string} imageUrl - AWS image url
 */
export const triggerViewTracking = (imageUrl) => {
  let imageId = '';

  try {
    /**
     * Unsplash images id are of two types `8ghkayi` and `-8ah2nso`
     */
    const unsplashIdString = imageUrl && imageUrl.split('unsplash-')[1];
    imageId = unsplashIdString && unsplashIdString.split('-')[0];

    /**
     * In the below code, I am checking if the imageId is the empty string.
     * If it is an empty string then I know the id contains one hyphen in the begining and
     * then I am fetching the value from next index because we are splitting it by `-`.
     */
    if (!imageId) {
      /**
       * This will return only the id of the image without hyphen, in the second
       * line `-` is concatenated with the id.
       */
      imageId = unsplashIdString && unsplashIdString.split('-')[1];
      imageId = `-${imageId}`;
    }
  } catch (error) {
    imageId = '';
  }

  // When an image is viewed on the participation side then this will get called to increment view count in unsplash
  fetch(
    `https://views.unsplash.com/v?app_id=${process.env.NEXT_PUBLIC_UNSPLASH_APP_ID}&photo_id=${imageId}`,
  );
};

/**
 *
 * @param {string} id - Image id
 */
const triggerDownloadTracking = async (photoId) => {
  const result = await unsplash.photos.get({ photoId });

  if (result.type === 'success') {
    const photo = result.response;
    unsplash.photos.trackDownload({
      downloadLocation: photo.links.download_location,
    });
  }
};

/**
 *
 * @param {string} imageUrl - The image URL
 * @param {number} minHeight - The height of the image
 */
export const loadImage = (imageUrl, height, width) => {
  if (imageUrl?.includes('/static/images/')) {
    const match = imageUrl.match(/\/(\d+)-/);

    if (match && match[1]) {
      const number = match[1];
      const newImageUrl = `/static/assets/images/home-page-placeholder-images/${number}.jpg`
      return newImageUrl
    } else {
      console.log('Number not found in the string.');
    }
  }
  // Return empty string if image URL is undefined
  if (!imageUrl) return '';

  // Return original image URL if the image is in .gif format
  if (imageUrl && imageUrl.indexOf('.gif') > -1) {
    return imageUrl;
  }

  const imgixUrl = buildImgixUrl(imageUrl, height, width);

  // Return imgix URL if the image is not downloaded from unsplash
  if (imageUrl && imageUrl.indexOf('unsplash-') === -1) return imgixUrl;

  /**
   * This function triggers the view tracker API call for unsplash image
   */
  triggerViewTracking(imageUrl);

  return imgixUrl;
};

/**
 *
 * @param {string} imageUrl
 */
export const fetchUnsplashImageId = (imageUrl) => {
  let imageId = '';

  try {
    /**
     * Unsplash images id are of two types `8ghkayi` and `-8ah2nso`
     */
    const unsplashIdString = imageUrl && imageUrl.split('unsplash-')[1];
    imageId = unsplashIdString && unsplashIdString.split('-')[0];

    /**
     * In the below code, I am checking if the imageId is the empty string.
     * If it is an empty string then I know the id contains one hyphen in the begining and
     * then I am fetching the value from next index because we are splitting it by `-`.
     */
    if (!imageId) {
      /**
       * This will return only the id of the image without hyphen, in the second
       * line `-` is concatenated with the id.
       */
      imageId = unsplashIdString && unsplashIdString.split('-')[1];
      imageId = `-${imageId}`;
    }
  } catch (error) {
    imageId = '';
  }

  return imageId;
};
